import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useListPrepaidSchedules, useRecognisePrepaidSchedules, getListPrepaidSchedulesQueryKey } from "@workspace/api-client-react";
import type { PrepaidSchedule, ListPrepaidSchedulesStatus } from "@workspace/api-client-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { inr } from "@/lib/format";

function progressStrip(schedule: PrepaidSchedule): string {
  const recognised = schedule.periods.filter((p) => p.recognisedAt != null).length;
  const pending = schedule.periods.length - recognised;
  const perMonth = schedule.months > 0 ? schedule.totalAmount / schedule.months : schedule.totalAmount;
  return `${inr(perMonth)} × ${schedule.months} — ${recognised} recognised, ${pending} pending`;
}

export function PrepaidTab() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [status, setStatus] = useState<ListPrepaidSchedulesStatus>("active");
  const { data: schedules, isLoading } = useListPrepaidSchedules({ status });
  const recogniseMut = useRecognisePrepaidSchedules();

  async function handleRunRecognition() {
    try {
      const result = await recogniseMut.mutateAsync({ data: {} });
      // `{ recognised, skipped, quarantined, scheduleIds }` on the wire —
      // 57-09's generated contract only declares `{ recognised, skipped }`
      // (same documented gap as this plan's other divergent endpoints).
      const withQuarantined = result as { recognised: number; skipped: number; quarantined?: number };
      qc.invalidateQueries({ queryKey: getListPrepaidSchedulesQueryKey() });
      const quarantined = withQuarantined.quarantined ?? 0;
      toast({
        title: `Recognised ${withQuarantined.recognised}, skipped ${withQuarantined.skipped}${quarantined > 0 ? `, ${quarantined} quarantined` : ""}.`,
        description: quarantined > 0 ? "Some months quarantined instead of posting — see Ledger → Quarantine." : undefined,
        variant: quarantined > 0 ? "destructive" : "default",
      });
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : "Could not run recognition.", variant: "destructive" });
    }
  }

  return (
    <div className="mt-4 space-y-3">
      <div className="flex items-center justify-between">
        <Select value={status} onValueChange={(v) => setStatus(v as ListPrepaidSchedulesStatus)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={handleRunRecognition} disabled={recogniseMut.isPending}>
          {recogniseMut.isPending ? "Running…" : "Run recognition"}
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      ) : (schedules ?? []).length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            No {status} prepaid schedules. A prepaid schedule is created automatically when a bill is approved with
            the Prepaid flag set.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {schedules!.map((s) => (
            <Card key={s.id}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">
                    Bill #{s.billId} · {s.expenseAccountCode}
                  </p>
                  <p className="text-xs text-muted-foreground">{progressStrip(s)}</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-mono tabular-nums">{inr(s.totalAmount)}</span>
                  <Badge variant={s.status === "completed" ? "secondary" : s.status === "cancelled" ? "outline" : "default"}>
                    {s.status}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
